/* eslint-disable import/no-unresolved */
import React, { useState } from 'react';
import {
  PayPalScriptProvider,
  PayPalButtons,
  PayPalButtonsComponentProps
} from '@paypal/react-paypal-js';
import { PayPalScriptOptions } from '@paypal/paypal-js/types/script-options';
import { OnApproveData } from '@paypal/paypal-js/types/components/buttons';
import { PAYPAL_CLIENT_ID } from './config/constants';
import {
  useCapturePaypalOrderMutation,
  useCreatePaypalOrderMutation
} from './graphql/graphql';
import toastMessage from './utils/toast';

type PaypalButtonProps = {
  bookingUuid: string;
  redirect: string;
  locale?: string | null;
};

const returnToApp = (redirect: string, success: boolean, err?: string) => {
  if (success) {
    window.location.href = redirect + '?success=true';
  } else {
    window.location.href = redirect + '?success=false&error=' + err;
  }
};

const returnButtonTranslations: { [key: string]: string } = {
  en: 'Return to app',
  fr: "Retour à l'application",
  es: 'Volver a la aplicación'
};

function PayPalButton({ bookingUuid, redirect, locale }: PaypalButtonProps) {
  const [createPaypalOrder] = useCreatePaypalOrderMutation();
  const [capturePaypalOrder] = useCapturePaypalOrderMutation();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState({
    value: false,
    message: ''
  });

  const submitCreatePaypalOrder = async (): Promise<string> => {
    try {
      if (!bookingUuid) return Promise.resolve('');
      const response = await createPaypalOrder({
        variables: { bookingUuid: bookingUuid }
      });
      return Promise.resolve(
        response.data?.createPaypalOrder?.orderId as string
      );
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const paypalbuttonTransactionProps: PayPalButtonsComponentProps = {
    style: {
      layout: 'horizontal',
      color: 'white',
      shape: 'rect',
      label: 'pay',
      height: 38
    },
    createOrder() {
      return submitCreatePaypalOrder()
        .then((orderId) => orderId)
        .catch((err) => {
          return err;
        });
    },
    onApprove(data: OnApproveData) {
      return capturePaypalOrder({ variables: { orderId: data.orderID } })
        .then((captureOrderData) => {
          if (captureOrderData.data?.capturePaypalOrder) {
            setPaymentSuccess(true);
            setTimeout(() => returnToApp(redirect, true), 500);
          } else {
            setPaymentError({ value: true, message: 'Payment not approved' });
            setTimeout(() => returnToApp(redirect, false), 500);
          }
        })
        .catch((err: any) => {
          return err;
        });
    },
    onError(err: any) {
      setTimeout(() => toastMessage(err.message, 'error'), 100);
      setPaymentError({ value: true, message: err.message });
      setTimeout(() => returnToApp(redirect, false, err?.message), 500);
    }
  };

  // Display a button to return to the app, chrome requires user initiated events for deeplink navigation
  if (paymentSuccess) {
    return (
      <button
        className="return-button"
        onClick={() => returnToApp(redirect, true)}
      >
        {returnButtonTranslations[locale?.substring(0, 2) || 'en']}
      </button>
    );
  }

  // Display a button to return to the app, chrome requires user initiated events for deeplink navigation
  if (paymentError.value) {
    return (
      <button
        className="return-button"
        onClick={() => returnToApp(redirect, false)}
      >
        {returnButtonTranslations[locale?.substring(0, 2) || 'en']}
      </button>
    );
  }

  return <PayPalButtons {...paypalbuttonTransactionProps} />;
}

type PaypalProps = {
  bookingUuid: string;
  redirect: string;
  locale?: string | null;
};

const Paypal = ({ bookingUuid, redirect, locale }: PaypalProps) => {
  const paypalScriptOptions: PayPalScriptOptions = {
    'client-id': PAYPAL_CLIENT_ID as string,
    currency: 'EUR',
    commit: false,
    locale: locale || 'en_US'
  };

  return (
    <PayPalScriptProvider options={paypalScriptOptions}>
      <PayPalButton
        bookingUuid={bookingUuid}
        redirect={redirect}
        locale={locale}
      />
    </PayPalScriptProvider>
  );
};

export default Paypal;
